/**
 * React Query hooks for closed-loop analytics endpoints.
 * Follows the useAuthenticatedQuery pattern from useWorkoutTemplates.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchLoadCurves,
  fetchAdherence,
  fetchTimeInZone,
  fetchProjection,
  fetchInjuryRisk,
  fetchTrends,
  fetchZones,
  fetchZoneSystem,
  bootstrapZones,
  updateZoneAnchor,
  calibratePaceZones,
  fetchSessionCompliance,
  fetchPerformanceAnalytics,
} from "@/lib/analyticsApi";
import type { PaceZoneDefinition } from "@/lib/analyticsApi";
import { useAuthState } from "./useAuthState";

// ── Query Keys ──────────────────────────────────────────────────────────────

const KEYS = {
  loadCurves: "loadCurves",
  adherence: "adherence",
  timeInZone: "timeInZone",
  projection: "projection",
  injuryRisk: "injuryRisk",
  trends: "trends",
  zones: "zones",
  sessionCompliance: "sessionCompliance",
  performancePaceZones: "performancePaceZones",
} as const;

// ── Analytics Queries ───────────────────────────────────────────────────────

export function useLoadCurves(start?: string, end?: string) {
  const { isLoaded, isAuthenticated } = useAuthState();
  return useQuery({
    queryKey: [KEYS.loadCurves, start, end],
    queryFn: () => fetchLoadCurves(start, end),
    enabled: isLoaded && isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAdherence(weeks?: number) {
  const { isLoaded, isAuthenticated } = useAuthState();
  return useQuery({
    queryKey: [KEYS.adherence, weeks],
    queryFn: () => fetchAdherence(weeks),
    enabled: isLoaded && isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
}

export function useTimeInZone(start?: string, end?: string, metric?: string) {
  const { isLoaded, isAuthenticated } = useAuthState();
  return useQuery({
    queryKey: [KEYS.timeInZone, start, end, metric],
    queryFn: () => fetchTimeInZone(start, end, metric),
    enabled: isLoaded && isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
}

export function useProjection(weeks?: number, raceDate?: string) {
  const { isLoaded, isAuthenticated } = useAuthState();
  return useQuery({
    queryKey: [KEYS.projection, weeks, raceDate],
    queryFn: () => fetchProjection(weeks, raceDate),
    enabled: isLoaded && isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
}

export function useInjuryRisk() {
  const { isLoaded, isAuthenticated } = useAuthState();
  return useQuery({
    queryKey: [KEYS.injuryRisk],
    queryFn: () => fetchInjuryRisk(),
    enabled: isLoaded && isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
}

export function useTrends(metrics?: string, weeks?: number) {
  const { isLoaded, isAuthenticated } = useAuthState();
  return useQuery({
    queryKey: [KEYS.trends, metrics, weeks],
    queryFn: () => fetchTrends(metrics, weeks),
    enabled: isLoaded && isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Session Compliance ──────────────────────────────────────────────────────

export function useSessionCompliance(sessionId: string | null | undefined) {
  const { isLoaded, isAuthenticated } = useAuthState();
  return useQuery({
    queryKey: [KEYS.sessionCompliance, sessionId],
    queryFn: () => fetchSessionCompliance(sessionId!),
    enabled: isLoaded && isAuthenticated && !!sessionId,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
}

// ── Zone Queries ────────────────────────────────────────────────────────────

export function useZones() {
  const { isLoaded, isAuthenticated } = useAuthState();
  return useQuery({
    queryKey: [KEYS.zones],
    queryFn: () => fetchZones(),
    enabled: isLoaded && isAuthenticated,
    staleTime: 10 * 60 * 1000,
  });
}

export function useZoneSystem(sport: string | null, metric: string | null) {
  const { isLoaded, isAuthenticated } = useAuthState();
  return useQuery({
    queryKey: [KEYS.zones, sport, metric],
    queryFn: () => fetchZoneSystem(sport!, metric!),
    enabled: isLoaded && isAuthenticated && !!sport && !!metric,
    staleTime: 10 * 60 * 1000,
  });
}

// ── Performance Pace Zones (fallback from /analytics/performance) ───────────

export interface PerformancePaceZones {
  zones: PaceZoneDefinition[];
  threshold_pace_ms: number | null;
  threshold_pace_source: 'configured' | 'inferred' | null;
}

export function usePerformancePaceZones() {
  const { isLoaded, isAuthenticated } = useAuthState();
  return useQuery({
    queryKey: [KEYS.performancePaceZones],
    queryFn: async (): Promise<PerformancePaceZones> => {
      const perf = await fetchPerformanceAnalytics(90);
      return {
        zones: perf.pace_zone_definitions ?? [],
        threshold_pace_ms: perf.threshold_pace_ms ?? null,
        threshold_pace_source: perf.threshold_pace_source ?? null,
      };
    },
    enabled: isLoaded && isAuthenticated,
    staleTime: 10 * 60 * 1000,
  });
}

// ── Zone Mutations ──────────────────────────────────────────────────────────

export function useBootstrapZones() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => bootstrapZones(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEYS.zones] });
    },
  });
}

export function useUpdateZoneAnchor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      zoneSystemId,
      anchorValue,
    }: {
      zoneSystemId: string;
      anchorValue: number;
    }) => updateZoneAnchor(zoneSystemId, anchorValue),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEYS.zones] });
    },
  });
}

export function useCalibratePaceZones() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => calibratePaceZones(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEYS.zones] });
      qc.invalidateQueries({ queryKey: [KEYS.performancePaceZones] });
    },
  });
}
